import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";

const piideoImages = [
  `${process.env.PUBLIC_URL}/images/img-piideo-1.png`,
  `${process.env.PUBLIC_URL}/images/img-piideo-2.png`,
  `${process.env.PUBLIC_URL}/images/img-piideo-3.png`,
];

const mcknightImages = [
  `${process.env.PUBLIC_URL}/images/mcknight-0.jpeg`,
  `${process.env.PUBLIC_URL}/images/mcknight-1.jpeg`,
  `${process.env.PUBLIC_URL}/images/mcknight-2.jpeg`,
  `${process.env.PUBLIC_URL}/images/mcknight-3.jpeg`,
];

const hocpayImages = [
  `${process.env.PUBLIC_URL}/images/pics-phone-1.jpeg`,
  `${process.env.PUBLIC_URL}/images/pics-phone-2.jpeg`,
];

const trafeekImages = [`${process.env.PUBLIC_URL}/images/trafeek-1.png`];

const useCycle = (length, interval = 3000) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % length), interval);
    return () => clearInterval(id);
  }, [length, interval]);
  return index;
};

const ProjectCard = ({ img, title, role, tech, desc, link }) => (
  <a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    className="project-card reveal"
  >
    <div className="project-card-media">
      <img src={img} alt={title} />
      <div className="project-card-overlay">
        <span>
          View Project <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </span>
      </div>
    </div>
    <div className="project-card-body">
      <h4>{title}</h4>
      <span className="role">{role}</span>
      <p>{desc}</p>
      <div className="project-stack">{tech.join(" · ")}</div>
    </div>
  </a>
);

const Projects = () => {
  const piideoIndex = useCycle(piideoImages.length);
  const mcknightIndex = useCycle(mcknightImages.length);
  const hocpayIndex = useCycle(hocpayImages.length);

  return (
    <section className="section" id="projects">
      <div className="section-inner">
        <div className="section-head reveal">
          <div className="eyebrow">Selected Work</div>
          <h2 className="section-title">Featured Projects</h2>
          <p className="section-subtitle">
            Products I've designed, built, and shipped to real users.
          </p>
        </div>

        <div className="featured-project reveal">
          <div className="featured-media">
            <img
              src={trafeekImages[0]}
              alt="Trafeek real-time traffic platform"
            />
          </div>
          <div className="featured-content">
            <div className="project-meta">
              Remote · Dec 2024 – Present · trafeek.app
            </div>
            <h3>Trafeek</h3>
            <p>
              Nigeria's first community-powered real-time traffic reporting
              platform, launched and scaled to{" "}
              <strong>1,000+ active users</strong> during its initial release. I
              own the full product lifecycle across three independent codebases:
              a React/TypeScript web app, a Node.js/TypeScript REST API, and a
              separate administrative dashboard: covering architecture,
              deployment, and monitoring.
            </p>
            <p>
              Under the hood: geolocation services and interactive live map
              tracking, role-based access control, subscription billing, and
              real-time push notifications, all built and maintained solo.
            </p>
            <div className="project-stack">
              React, TypeScript, Node.js, REST APIs, Geolocation, Push
              Notifications
            </div>
            <a
              href="https://trafeek.app"
              className="btn btn-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit trafeek.app
            </a>
          </div>
        </div>

        <div className="featured-project reveal reverse">
          <div className="featured-media placeholder">
            <div className="placeholder-badge">P</div>
            <h4 className="placeholder-title">Preggimum</h4>
            <span className="placeholder-tag">
              Private Beta &middot; Launching 2026
            </span>
          </div>
          <div className="featured-content">
            <div className="project-meta">
              <span className="status-dot status-dot-beta" />
              In private beta · 2026
            </div>
            <h3>Preggimum</h3>
            <p>
              A cross-platform pregnancy and fertility app spanning{" "}
              <strong>71 screens across ten feature domains</strong>, from
              appointments and community to resources and personal health
              tracking, shipped to iOS and Android from a single Expo and React
              Native codebase.
            </p>
            <p>
              I architected authentication, profiles, and CMS content on a
              self-hosted Node and Azure REST API, and built real-time video
              consultations between users and providers using LiveKit and
              WebRTC. The companion admin platform runs on Blazor and ASP.NET
              Core (.NET 10), backed by SQL Server and EF Core, with push
              notifications, scheduled reminders, and OTA updates delivered
              through Expo.
            </p>
            <div className="project-stack">
              React Native, TypeScript, Expo, .NET, Azure, LiveKit, Blazor, SQL
              Server
            </div>
          </div>
        </div>

        <div className="featured-project reveal">
          <div className="featured-media">
            <img
              src={hocpayImages[hocpayIndex]}
              alt="HocPay mobile screenshots"
            />
          </div>
          <div className="featured-content">
            <div className="project-meta">Fintech · Nigeria</div>
            <h3>HocPay</h3>
            <p>
              A secure mobile wallet for everyday payments in Nigeria, reaching{" "}
              <strong>1,000+ active users</strong>. I architected and built the
              full application: mobile app, backend APIs, admin dashboard, and
              payment integrations, with real-time payment processing, secure
              transaction logging, and offline caching for low-connectivity
              regions.
            </p>
            <div className="project-stack">
              Flutter, Node.js, Firebase, AWS EC2, Docker, REST APIs
            </div>
            <div className="store-buttons">
              <a
                href="https://apps.apple.com/ng/app/hocpay/id6751758646"
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download on iOS
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.jaysmart.hocpay&pcampaignid=web_share"
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download on Android
              </a>
            </div>
          </div>
        </div>

        <div className="projects-grid">
          <ProjectCard
            img={mcknightImages[mcknightIndex]}
            title="McKnight-Man"
            role="Web Developer"
            tech={["Next.js", "React", "Node.js", "AWS", "Docker"]}
            desc="E-commerce platform for a Paris fashion brand. Built custom translation system for multilingual support and a dynamic pricing engine for international currencies."
            link="https://www.mcknight-man.com"
          />

          <ProjectCard
            img={piideoImages[piideoIndex]}
            title="Piideo"
            role="Mobile Developer"
            tech={["Flutter", "Firebase", "REST APIs"]}
            desc="Fashion discovery app celebrating African designers. Features video galleries, offline caching, and personalized recommendations."
            link="https://www.piideo.com/"
          />
        </div>
      </div>
    </section>
  );
};

export default Projects;
